import { formatDate } from '../../dates.ts';
import { DEATH_TASK_TAG, DEATH_TASK_WORDS, isDeathTask, rowMarks, type Task } from '../../tasks/model.ts';
import { StatusTag } from '../StatusTag.tsx';
import { formatInstant } from '../../tasks/fields.ts';

/**
 * One task in any list (my tasks, the queue, the dashboard). A button: it opens the task card. Emergency and ASAP
 * are said in words, never by colour alone (Arthur, A-#78); an overdue Emergency is in Overdue, marked Emergency.
 */
export function TaskRow({ task: t, today, onOpen, showHolder = false }: {
  task: Task; today: string; onOpen: (id: string) => void; showHolder?: boolean;
}) {
  const { word, overdue, doneDirectly, estimate } = rowMarks(t, today);
  const done = t.status === 'Done' ? formatInstant(t.completion?.recorded_at ?? t.times.completed_at) : null;
  return (
    <li data-task-id={t.id} data-quick-client={t.client?.id}>
      <button type="button" onClick={() => onOpen(t.id)}
              className="flex min-h-[64px] w-full gap-3 px-4 py-3 text-left hover:bg-neutral-50 active:bg-neutral-100 lg:items-center lg:px-5">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            {word && (
              <span className={`rounded px-1.5 py-0.5 text-xs font-bold tracking-wide uppercase ${
                t.severity === 'Critical' ? 'bg-critical-bg text-critical-fg' : 'border border-neutral-400 text-neutral-800'}`}>
                {word}
              </span>
            )}
            <span className="font-semibold text-neutral-900">{t.title}</span>
          </span>
          {t.client && (
            <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[0.9375rem] text-neutral-700">
              <span className="truncate">{t.client.name}</span>
              <span className="font-mono text-sm text-neutral-600">{t.client.ref}</span>
              <StatusTag status={t.client.status} />
            </span>
          )}
          <span className="mt-0.5 block text-sm text-neutral-700">
            {t.department}
            {isDeathTask(t) && (
              // F-29: words, never colour alone; the full sentence for a screen reader and on hover
              <span className="ml-2 rounded border border-neutral-400 px-1.5 py-0.5 text-xs font-semibold text-neutral-800"
                    aria-label={DEATH_TASK_WORDS} title={DEATH_TASK_WORDS}>
                {DEATH_TASK_TAG}
              </span>
            )}
            {showHolder && ` · ${t.assigned_to ? t.assigned_to.name : 'Unassigned: the queue'}`}
            {t.status === 'In Progress' && ' · In progress'}
            {t.status === 'Blocked' && ' · Blocked'}
            {doneDirectly && ' · Done directly'}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-sm text-neutral-800 tabular-nums">
            {done ? `Done ${done}` : t.due_date ? formatDate(t.due_date) : 'No due date'}
          </span>
          {!done && t.due_date && estimate && <span className="block text-xs text-neutral-700">estimate</span>}
          {overdue && <span className="block text-sm font-semibold text-critical-edge">Overdue</span>}
          {t.is_statutory && <span className="block text-xs font-bold tracking-wide text-neutral-700 uppercase">Statutory</span>}
        </span>
      </button>
    </li>
  );
}
